import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, Snackbar, Alert, Tooltip, Tabs, Tab, InputAdornment,
  FormControlLabel, Switch, Select
} from '@mui/material';
import {
  Article as ArticleIcon, Refresh as RefreshIcon, Add as AddIcon,
  Edit as EditIcon, Delete as DeleteIcon, AutoAwesome as AutoAwesomeIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  Link as LinkIcon, Search as SearchIcon, ArrowUpward as PublishIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

const ADMIN_ARTICLE_API = `${API_BASE_URL}/kOjtx3AXNmCsT5Wz/articles`;

const CATEGORY_OPTIONS = ['AI写作', '网文创作', '剧本改编', '行业观察'];

const EMPTY_FORM = {
  title: '',
  slug: '',
  summary: '',
  category: 'AI写作',
  tags: '',
  content: '',
  metaTitle: '',
  metaDescription: '',
  status: 'draft'
};

// 轻量Markdown渲染（编辑器预览用，与后端marked渲染效果近似）
const renderMarkdownPreview = (md) => {
  const escapeHtml = (s) => s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const blocks = String(md || '').split(/\n{2,}/);
  return blocks.map((block) => {
    const lines = block.split('\n');
    const first = lines[0] || '';

    const h = /^(#{1,4})\s+(.+)$/.exec(first);
    if (h) {
      const level = h[1].length;
      return `<h${level}>${escapeHtml(h[2])}</h${level}>`;
    }
    if (/^>\s?/.test(first)) {
      return `<blockquote>${escapeHtml(block.replace(/^>\s?/gm, ''))}</blockquote>`;
    }
    if (/^(-{3,}|\*{3,})$/.test(first.trim())) return '<hr/>';
    if (/^```/.test(first)) {
      return `<pre><code>${escapeHtml(lines.slice(1, -1).join('\n'))}</code></pre>`;
    }
    if (/^\s*[-*]\s+/.test(first)) {
      const items = lines.filter((l) => /^\s*[-*]\s+/.test(l))
        .map((l) => `<li>${escapeHtml(l.replace(/^\s*[-*]\s+/, ''))}</li>`).join('');
      return `<ul>${items}</ul>`;
    }
    if (/^\s*\d+\.\s+/.test(first)) {
      const items = lines.filter((l) => /^\s*\d+\.\s+/.test(l))
        .map((l) => `<li>${escapeHtml(l.replace(/^\s*\d+\.\s+/, ''))}</li>`).join('');
      return `<ol>${items}</ol>`;
    }

    // 行内格式：加粗 / 代码 / 链接
    let html = escapeHtml(block);
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    html = html.replace(/\n/g, '<br/>');
    return `<p>${html}</p>`;
  }).join('\n');
};

const ArticleManagement = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // 编辑器
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // AI草稿
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showMessage = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
  });

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page + 1 });
      if (keyword.trim()) params.set('keyword', keyword.trim());
      if (statusFilter) params.set('status', statusFilter);
      const response = await fetch(`${ADMIN_ARTICLE_API}?${params}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setArticles(data.data.articles);
        setTotal(data.data.total);
      } else {
        showMessage(data.message || '获取文章列表失败', 'error');
      }
    } catch (error) {
      console.error('获取文章失败:', error);
      showMessage('获取文章列表失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, keyword, statusFilter]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setPreviewMode(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (id) => {
    try {
      const response = await fetch(`${ADMIN_ARTICLE_API}/${id}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        const a = data.data;
        setEditingId(a._id);
        setForm({
          title: a.title || '',
          slug: a.slug || '',
          summary: a.summary || '',
          category: a.category || 'AI写作',
          tags: (a.tags || []).join(', '),
          content: a.content || '',
          metaTitle: a.metaTitle || '',
          metaDescription: a.metaDescription || '',
          status: a.status || 'draft'
        });
        setPreviewMode(false);
        setDialogOpen(true);
      } else {
        showMessage('获取文章详情失败', 'error');
      }
    } catch (error) {
      showMessage('获取文章详情失败', 'error');
    }
  };

  // slug自动生成
  const handleGenerateSlug = async () => {
    if (!form.title.trim()) {
      showMessage('请先填写文章标题', 'warning');
      return;
    }
    try {
      const response = await fetch(`${ADMIN_ARTICLE_API}/generate-slug`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title: form.title })
      });
      const data = await response.json();
      if (data.success) {
        setForm((f) => ({ ...f, slug: data.data.slug }));
        showMessage('slug已生成');
      } else {
        showMessage(data.message || 'slug生成失败', 'error');
      }
    } catch (error) {
      showMessage('slug生成失败', 'error');
    }
  };

  // AI草稿生成
  const handleAiGenerate = async () => {
    if (!aiTopic.trim()) {
      showMessage('请输入文章主题', 'warning');
      return;
    }
    setAiGenerating(true);
    try {
      const response = await fetch(`${ADMIN_ARTICLE_API}/ai-draft`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ topic: aiTopic.trim(), keywords: aiKeywords.trim() })
      });
      const data = await response.json();
      if (data.success) {
        const draft = data.data;
        setForm({
          title: draft.title || '',
          slug: '',
          summary: draft.summary || '',
          category: CATEGORY_OPTIONS.includes(draft.category) ? draft.category : 'AI写作',
          tags: (draft.tags || []).join(', '),
          content: draft.content || '',
          metaTitle: '',
          metaDescription: '',
          status: 'draft'
        });
        setAiDialogOpen(false);
        setEditingId(null);
        setPreviewMode(false);
        setDialogOpen(true);
        showMessage('AI草稿已生成，请检查后保存');
      } else {
        showMessage(data.message || 'AI草稿生成失败', 'error');
      }
    } catch (error) {
      showMessage('AI草稿生成失败', 'error');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { showMessage('请填写文章标题', 'warning'); return; }
    if (!form.content.trim()) { showMessage('请填写文章正文', 'warning'); return; }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        summary: form.summary.trim(),
        category: form.category,
        tags: form.tags.split(/[,，]/).map((t) => t.trim()).filter(Boolean),
        content: form.content,
        metaTitle: form.metaTitle.trim(),
        metaDescription: form.metaDescription.trim(),
        status: form.status
      };
      const url = editingId ? `${ADMIN_ARTICLE_API}/${editingId}` : ADMIN_ARTICLE_API;
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setDialogOpen(false);
        fetchArticles();
        const pushNote = data.baiduPush
          ? (data.baiduPush.skipped ? '（未配置百度推送token）' : '，已推送百度收录')
          : '';
        showMessage(`${form.status === 'published' ? '文章已发布' : '文章已保存'}${pushNote}`);
      } else {
        showMessage(data.message || '保存失败', 'error');
      }
    } catch (error) {
      showMessage('保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 快速发布/下架
  const handleToggleStatus = async (article) => {
    const next = article.status === 'published' ? 'draft' : 'published';
    try {
      const response = await fetch(`${ADMIN_ARTICLE_API}/${article._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: next })
      });
      const data = await response.json();
      if (data.success) {
        fetchArticles();
        showMessage(next === 'published' ? '文章已发布' : '文章已下架');
      } else {
        showMessage(data.message || '操作失败', 'error');
      }
    } catch (error) {
      showMessage('操作失败', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定删除该文章？删除后不可恢复。')) return;
    try {
      const response = await fetch(`${ADMIN_ARTICLE_API}/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await response.json();
      if (data.success) {
        fetchArticles();
        showMessage('文章已删除');
      } else {
        showMessage(data.message || '删除失败', 'error');
      }
    } catch (error) {
      showMessage('删除失败', 'error');
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString('zh-CN', { hour12: false }) : '-');

  return (
    <Box sx={{ py: 3, maxWidth: 1400, mx: 'auto', px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: 3 }}>
        {/* 标题栏 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <ArticleIcon sx={{ color: '#00ff9f', fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ color: '#00ff9f', fontWeight: 'bold' }}>
                文章管理
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                SEO内容发布 · 已发布 {total} 篇（含草稿）
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchArticles} sx={{ borderColor: '#00ff9f', color: '#00ff9f' }}>
              刷新
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setAiDialogOpen(true)}
              sx={{ borderColor: '#64b5f6', color: '#64b5f6' }}
            >
              AI生成草稿
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ bgcolor: '#00ff9f', color: '#000', '&:hover': { bgcolor: '#00cc7f' } }}>
              新建文章
            </Button>
          </Box>
        </Box>

        {/* 筛选栏 */}
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <TextField
            size="small" placeholder="搜索标题/slug" value={keyword}
            onChange={(e) => { setPage(0); setKeyword(e.target.value); }}
            sx={{ width: 260 }}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>)
            }}
          />
          <Select
            size="small" value={statusFilter} displayEmpty
            onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}
            sx={{ width: 140 }}
          >
            <MenuItem value="">全部状态</MenuItem>
            <MenuItem value="published">已发布</MenuItem>
            <MenuItem value="draft">草稿</MenuItem>
          </Select>
        </Box>

        {/* 文章列表 */}
        {loading ? (
          <Box textAlign="center" py={6}><CircularProgress sx={{ color: '#00ff9f' }} /></Box>
        ) : articles.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Typography sx={{ color: 'text.secondary' }}>暂无文章，点击右上角新建或使用AI生成草稿</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(0, 30, 60, 0.5)' }}>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>标题</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>slug</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>分类</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>状态</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }} align="center">浏览</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>更新时间</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }} align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {articles.map((a) => (
                  <TableRow key={a._id} hover>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography noWrap sx={{ fontWeight: 600 }}>{a.title}</Typography>
                      <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>{a.summary}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="查看线上页面">
                        <Typography
                          noWrap variant="body2"
                          sx={{ color: '#64b5f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 200 }}
                          onClick={() => window.open(`/articles/${a.slug}`, '_blank')}
                        >
                          <LinkIcon sx={{ fontSize: 14 }} />{a.slug}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{a.category || '-'}</TableCell>
                    <TableCell>
                      <Chip label={a.status === 'published' ? '已发布' : '草稿'} size="small"
                        sx={a.status === 'published'
                          ? { bgcolor: 'rgba(0, 255, 159, 0.15)', color: '#00ff9f' }
                          : { bgcolor: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8' }} />
                    </TableCell>
                    <TableCell align="center">{a.views || 0}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{fmtDate(a.updatedAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="编辑">
                        <IconButton size="small" onClick={() => handleOpenEdit(a._id)} sx={{ color: '#64b5f6' }}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={a.status === 'published' ? '下架' : '发布'}>
                        <IconButton size="small" onClick={() => handleToggleStatus(a)}
                          sx={{ color: a.status === 'published' ? '#fbbf24' : '#00ff9f' }}>
                          {a.status === 'published' ? <VisibilityOffIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton size="small" onClick={() => handleDelete(a._id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={10}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </TableContainer>
        )}
      </Paper>

      {/* 编辑对话框 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: 'rgba(0, 21, 44, 0.98)', height: '88vh', maxHeight: '900px' } }}>
        <DialogTitle sx={{ color: '#00ff9f', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingId ? '编辑文章' : '新建文章'}
          <Tabs value={previewMode ? 1 : 0} onChange={(e, v) => setPreviewMode(v === 1)} sx={{ minHeight: 0 }}>
            <Tab label="编辑" sx={{ minHeight: 36, fontSize: '0.85rem' }} />
            <Tab label="预览" sx={{ minHeight: 36, fontSize: '0.85rem' }} />
          </Tabs>
        </DialogTitle>
        <DialogContent dividers>
          {previewMode ? (
            <Box sx={{ '& h1': { my: 2, fontSize: '1.6rem' }, '& h2': { my: 2, fontSize: '1.35rem', pl: 1.5, borderLeft: '3px solid #3b82f6' }, '& h3': { my: 1.5, fontSize: '1.15rem' }, '& p': { my: 1.5, lineHeight: 1.9 }, '& li': { ml: 3, my: 0.5, lineHeight: 1.8 }, '& blockquote': { my: 2, p: 1.5, pl: 2, borderLeft: '3px solid #3b82f6', bgcolor: 'rgba(59,130,246,0.07)', borderRadius: 1, color: 'text.secondary' }, '& pre': { my: 2, p: 2, bgcolor: 'rgba(0,0,0,0.4)', borderRadius: 1.5, overflowX: 'auto' }, '& code': { fontFamily: 'Consolas, monospace', bgcolor: 'rgba(59,130,246,0.13)', px: 0.8, borderRadius: 0.5, fontSize: '0.9em', color: '#22d3ee' }, '& a': { color: '#64b5f6' }, '& hr': { my: 3, borderColor: 'rgba(148,163,184,0.2)' } }}
              dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(form.content) }} />
          ) : (
            <Box display="grid" gap={2} sx={{ pt: 1 }}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField label="文章标题" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  sx={{ flex: 2, minWidth: 300 }} size="small" />
                <TextField label="分类" select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  sx={{ width: 140 }} size="small">
                  {CATEGORY_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField select label="状态" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  sx={{ width: 110 }} size="small">
                  <MenuItem value="draft">草稿</MenuItem>
                  <MenuItem value="published">发布</MenuItem>
                </TextField>
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                <TextField label="slug（URL标识，留空自动生成）" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  sx={{ flex: 1 }} size="small" />
                <Button variant="outlined" size="small" onClick={handleGenerateSlug}
                  sx={{ borderColor: '#64b5f6', color: '#64b5f6', whiteSpace: 'nowrap' }}>
                  自动生成
                </Button>
              </Box>
              <TextField label="摘要（列表展示与默认meta描述，120字内）" value={form.summary}
                onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                multiline rows={2} size="small" inputProps={{ maxLength: 300 }} />
              <TextField label="标签（逗号分隔）" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                size="small" placeholder="AI写作, 网文创作, 教程" />
              <TextField label="正文（Markdown格式）" value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                multiline rows={16} size="small"
                sx={{ '& .MuiInputBase-input': { fontFamily: 'Consolas, "Courier New", monospace', fontSize: '13px', lineHeight: 1.8 } }}
                placeholder={'# 支持 Markdown\n\n## 二级标题会进入文章目录\n\n正文支持 **加粗**、`代码`、[链接](url)、列表、引用等格式'} />
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  SEO覆写（选填，不填则自动使用标题与摘要）
                </Typography>
                <Box display="grid" gap={2}>
                  <TextField label="SEO标题（meta title）" value={form.metaTitle}
                    onChange={(e) => setForm((f) => ({ ...f, metaTitle: e.target.value }))} size="small" />
                  <TextField label="SEO描述（meta description）" value={form.metaDescription}
                    onChange={(e) => setForm((f) => ({ ...f, metaDescription: e.target.value }))}
                    multiline rows={2} size="small" inputProps={{ maxLength: 200 }} />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: 'text.secondary' }}>取消</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#00ff9f', color: '#000', '&:hover': { bgcolor: '#00cc7f' }, minWidth: 100 }}>
            {saving ? <CircularProgress size={22} sx={{ color: '#000' }} /> : (form.status === 'published' ? '发布' : '保存草稿')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI草稿对话框 */}
      <Dialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'rgba(0, 21, 44, 0.98)' } }}>
        <DialogTitle sx={{ color: '#64b5f6', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon /> AI生成文章草稿
        </DialogTitle>
        <DialogContent dividers>
          <Box display="grid" gap={2} sx={{ pt: 1 }}>
            <TextField label="文章主题" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
              placeholder="如：新手如何用AI写出第一部网文小说" size="small" autoFocus />
            <TextField label="关键词（选填，逗号分隔）" value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)}
              placeholder="如：AI写作, 小说生成, 网文创作" size="small" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              AI将围绕主题生成标题、摘要、正文与标签的完整草稿，生成后自动填入编辑器，可继续修改后保存发布。
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAiDialogOpen(false)} sx={{ color: 'text.secondary' }}>取消</Button>
          <Button variant="contained" onClick={handleAiGenerate} disabled={aiGenerating}
            sx={{ bgcolor: '#64b5f6', color: '#000', '&:hover': { bgcolor: '#42a5f5' }, minWidth: 110 }}>
            {aiGenerating ? <CircularProgress size={22} sx={{ color: '#000' }} /> : '开始生成'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArticleManagement;
